import { useCallback, useEffect, useRef, useState } from "react";
import { BrandStrip } from "@/components/BrandFooter";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TelemetryHeader } from "@/components/TelemetryHeader";
import { TacticalMap } from "@/components/map/TacticalMap";
import { RegionMenu } from "@/components/map/MapControls";
import { LayerPanel } from "@/components/map/LayerPanel";
import { buildIncidents, TOP_N } from "@/lib/incidents";
import { PanelEdgeToggle } from "@/components/map/PanelEdgeToggle";
import { impactLine } from "@/components/map/CycloneLayer";
import { REGIONS, inRegion } from "@/lib/map";
import { DiscoveryLedger } from "@/components/DiscoveryLedger";
import { DetailDrawer } from "@/components/DetailDrawer";
import { Loader2, Layers, Activity, Flame, CloudRain, Mountain, Waves, Droplets, TriangleAlert } from "lucide-react";
import { getHazard, HAZARD_TITLE, HAZARD_LABEL } from "@/lib/hazard";
import { LeadTimeBanner } from "@/components/LeadTimeBanner";
import { AlertsDrawer } from "@/components/AlertsDrawer";
import { CalibrationPanel } from "@/components/CalibrationPanel";
import { BroadcastsPanel } from "@/components/BroadcastsPanel";
import { ForesightPanel } from "@/components/ForesightPanel";

export default function GlobalObservatory() {
  const [telemetry, setTelemetry] = useState(null);
  const [observations, setObservations] = useState([]);
  const [hypotheses, setHypotheses] = useState([]);
  const [viewMode, setViewMode] = useState("points");
  const [hazard, setHazard] = useState("all");
  const [country, setCountry] = useState("all");
  const [basemap, setBasemap] = useState("satellite");
  const [region, setRegion] = useState("global");
  const [shakemaps, setShakemaps] = useState([]);
  const [cyclones, setCyclones] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [reported, setReported] = useState([]);
  const [showRep, setShowRep] = useState(true);
  const [felt, setFelt] = useState([]);
  const [showFelt, setShowFelt] = useState(true);
  useEffect(() => {
    const get = () => api.feltQuakes().then((r) => setFelt(r.items || [])).catch(() => {});
    get();
    const t = setInterval(get, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const [dams, setDams] = useState([]);
  const [showDam, setShowDam] = useState(true);
  const [showCyc, setShowCyc] = useState(true);
  const [showShake, setShowShake] = useState(true);
  const [rainMeta, setRainMeta] = useState(null);
  const [showRain, setShowRain] = useState(false);
  const [rainMode, setRainMode] = useState("rate");
  const [rainOpacity, setRainOpacity] = useState(0.7);

  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [calibOpen, setCalibOpen] = useState(false);
  const [broadcastsOpen, setBroadcastsOpen] = useState(false);
  const [foresightOpen, setForesightOpen] = useState(false);
  const [foresightCount, setForesightCount] = useState(0);
  const [focus, setFocus] = useState(null);
  const [showAllInc, setShowAllInc] = useState(false);
  const [ledgerTab, setLedgerTab] = useState("main");
  const [showMon, setShowMon] = useState(false);
  const [fullMap, setFullMap] = useState(() => localStorage.getItem("atlas-layout") === "map");
  // Side panels open INSIDE the observations column (one at a time); in full-map mode it slides in first.
  const openPanel = (setter) => {
    [setAlertsOpen, setCalibOpen, setBroadcastsOpen, setForesightOpen].forEach((f) => f(false));
    setter(true);
    setFullMap(false);
  };
  const toggleFullMap = () => setFullMap((v) => {
    localStorage.setItem("atlas-layout", v ? "split" : "map");
    return !v;
  });
  const seenAlerts = useRef(new Set());
  const firstAlerts = useRef(true);

  const [booting, setBooting] = useState(true);
  const lastIngestRef = useRef(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [activeUid, setActiveUid] = useState(null);
  const booted = useRef(false);
  const failsRef = useRef(0);
  const [apiError, setApiError] = useState(null);

  const loadAll = useCallback(async () => {
    const [obs, hyps] = await Promise.all([api.observations(), api.hypotheses()]);
    setObservations(Array.isArray(obs) ? obs : []);
    setHypotheses(Array.isArray(hyps) ? hyps : []);
  }, []);

  const heartbeat = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const status = await api.telemetry();
      failsRef.current = 0;
      setApiError(null);
      setTelemetry(status);
      if (status.last_ingest_at && status.last_ingest_at !== lastIngestRef.current) {
        const first = lastIngestRef.current === null;
        lastIngestRef.current = status.last_ingest_at;
        await loadAll();
        if (!first) toast.success("Autonomous scan complete — observatory refreshed");
        setBooting(false);
      }
    } catch (e) {
      // engine may be mid-bootstrap on first run; only surface persistent or misrouted-API failures
      failsRef.current += 1;
      if (failsRef.current >= 3 || String(e?.message).includes("did not return JSON")) setApiError(e?.message || "Backend unreachable");
    }
  }, [loadAll]);

  const heartbeatRef = useRef(heartbeat);
  heartbeatRef.current = heartbeat;

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    heartbeatRef.current();
    const id = setInterval(() => heartbeatRef.current(), 12000);
    return () => clearInterval(id);
  }, []);

  // Context layers: USGS ShakeMap contours (refresh every 30 min) + NASA IMERG rainfall metadata.
  useEffect(() => {
    const load = () => {
      api.shakemaps().then((r) => setShakemaps(r.events || [])).catch(() => {});
      api.rainfall().then(setRainMeta).catch(() => {});
      api.cyclones().then((r) => setCyclones(r.storms || [])).catch(() => {});
      api.landslideDams().then((r) => setDams(r.items || [])).catch(() => {});
      api.events().then((r) => setReported((r || []).filter((e) => e.verified === false))).catch(() => {});
    };
    api.countries().then((r) => setCountryList(Array.isArray(r) ? r : [])).catch(() => {});
    load();
    const id = setInterval(load, 20 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const openDetail = useCallback(async (uid) => {
    setActiveUid(uid);
    setDrawerOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const d = await api.observation(uid);
      setDetail(d);
      const o = d?.observation;
      if (o?.triage_status === "unexplained_anomaly" && !(d.hypotheses || []).length) {
        api
          .hypothesize(uid)
          .then((r) => setDetail((cur) => (cur?.observation?.uid === uid ? { ...cur, hypotheses: r.hypotheses || [] } : cur)))
          .catch(() => {});
      }
    } catch (e) {
      setDetail(null);
      setDetailError("Source observation rotated out of the latest cycle");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const focusOn = useCallback((lat, lon) => setFocus({ lat, lon, key: Date.now() }), []);

  const selectShake = useCallback((e) => {
    setFocus({ lat: e.lat, lon: e.lon, zoom: 7, key: Date.now() });
    toast.info(e.title, {
      description: `Peak shaking MMI ${e.max_mmi} · USGS ShakeMap`,
      action: e.url ? { label: "USGS page", onClick: () => window.open(e.url, "_blank", "noopener") } : undefined,
    });
  }, []);

  const selectCyclone = useCallback((s) => {
    setFocus({ lat: s.lat, lon: s.lon, zoom: 5, key: Date.now() });
    toast.info(`${s.classification_label} ${s.name}`, {
      description: `${s.category}${s.wind_kt ? ` · ${Math.round(s.wind_kt)} kt` : ""}${s.pressure_mb ? ` · ${s.pressure_mb} mb` : ""} · ${s.source}${s.impact ? ` — ${impactLine(s)}` : ""}`,
      action: s.advisory_url ? { label: "Advisory", onClick: () => window.open(s.advisory_url, "_blank", "noopener") } : undefined,
    });
  }, []);

  const selectRegion = useCallback((r) => {
    setRegion(r.id);
    setCountry("all");
    setFocus(r.bounds ? { bounds: r.bounds, key: Date.now() } : { global: true, key: Date.now() });
  }, []);

  const selectObs = useCallback(
    (obs) => {
      if (obs) focusOn(obs.lat, obs.lon);
      openDetail(obs.uid);
    },
    [focusOn, openDetail]
  );

  const selectByUid = useCallback(
    (uid) => {
      const o = observations.find((x) => x.uid === uid);
      if (o) focusOn(o.lat, o.lon);
      openDetail(uid);
    },
    [observations, focusOn, openDetail]
  );

  const focusAlert = useCallback(
    (a) => {
      focusOn(a.lat, a.lon);
      setAlertsOpen(false);
      // Alerts persist across autonomous scans, so their original observation may
      // have been replaced. Re-link to the current live reading for the same
      // region/hazard (else the nearest same-hazard point) and open that.
      let o = observations.find((x) => x.uid === a.observation_uid);
      if (!o) o = observations.find((x) => getHazard(x) === a.hazard && x.region === a.region);
      if (!o) {
        let best = null;
        let bd = Infinity;
        for (const x of observations.filter((x) => getHazard(x) === a.hazard)) {
          const d = (x.lat - a.lat) ** 2 + (x.lon - a.lon) ** 2;
          if (d < bd) {
            bd = d;
            best = x;
          }
        }
        o = best;
      }
      if (o) openDetail(o.uid);
      else toast.info(`${a.region}: map focused (no live reading for this region right now)`);
    },
    [focusOn, openDetail, observations]
  );

  // When a country is selected, sweep the map to that country's observations.
  useEffect(() => {
    if (country === "all") return;
    const pts = observations.filter((o) => o.country === country);
    if (!pts.length) {
      const c = countryList.find((x) => x.name === country);
      if (c) setFocus({ bounds: c.bbox[0][0] === c.bbox[1][0] ? [[c.lat - 1, c.lon - 1], [c.lat + 1, c.lon + 1]] : c.bbox, key: Date.now() });
      return;
    }
    const lat = pts.reduce((s, o) => s + o.lat, 0) / pts.length;
    const lon = pts.reduce((s, o) => s + o.lon, 0) / pts.length;
    focusOn(lat, lon);
  }, [country, observations, focusOn, countryList]);


  const pollAlerts = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const list = await api.alerts();
      setAlerts(list);
      const fresh = list.filter((a) => !seenAlerts.current.has(a.uid));
      list.forEach((a) => seenAlerts.current.add(a.uid));
      if (!firstAlerts.current) {
        fresh.slice(0, 3).forEach((a) => {
          const fn = a.level === "critical" ? toast.error : toast.warning;
          fn(`${a.region} — ${a.title}`, {
            description: `${Math.round(a.confidence_score * 100)}% confidence · ${HAZARD_LABEL[a.hazard] || a.hazard} breakthrough`,
          });
        });
      }
      firstAlerts.current = false;
    } catch (e) {
      /* alerts are best-effort */
    }
    try {
      const f = await api.foresight();
      setForesightCount((f.bulletins || []).length);
    } catch {
      /* foresight is best-effort */
    }
  }, []);

  const ackAlert = useCallback(async (uid) => {
    try {
      await api.ackAlert(uid);
      setAlerts((prev) => prev.map((a) => (a.uid === uid ? { ...a, acknowledged: true } : a)));
    } catch (e) {
      toast.error("Failed to acknowledge");
    }
  }, []);

  const onBroadcast = useCallback(async (uid) => {
    try {
      await api.broadcastAlert(uid);
      toast.success("Broadcast queued — Vigilo Earth is generating the image…");
      openPanel(setBroadcastsOpen);
    } catch (e) {
      toast.error("Broadcast failed: " + e.message);
    }
  }, []);

  const pollAlertsRef = useRef(pollAlerts);
  pollAlertsRef.current = pollAlerts;

  useEffect(() => {
    pollAlerts();
    const id = setInterval(pollAlerts, 10000);
    return () => clearInterval(id);
  }, [pollAlerts]);

  // When the tab is refocused, refresh immediately instead of waiting for the poll.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) {
        heartbeatRef.current();
        pollAlertsRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const HAZARD_TABS = [
    ["all", Layers, "All"],
    ["seismic", Activity, "Seismic"],
    ["fire", Flame, "Fire"],
    ["storm", CloudRain, "Storms"],
    ["volcano", Mountain, "Volcano"],
    ["tsunami", Waves, "Tsunami"],
    ["flood", Droplets, "Flood"],
    ["landslide", TriangleAlert, "Landslide"],
  ];

  const HazardToggle = () => (
    <div className="flex border border-white/10 bg-atlas-void/80 backdrop-blur" data-testid="hazard-toggle">
      {HAZARD_TABS.map(([key, Icon, label]) => {
        const count = key === "all" ? null : telemetry?.by_hazard?.[key];
        return (
          <button
            key={key}
            onClick={() => setHazard(key)}
            data-testid={`hazard-${key}`}
            className={`flex items-center gap-1.5 px-3 h-8 text-[10px] font-mono uppercase tracking-wider transition-colors border-l border-white/10 first:border-l-0 ${
              hazard === key ? "bg-atlas-cyan text-atlas-void" : "text-atlas-secondary hover:text-white"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {count != null ? <span className="opacity-70">{count}</span> : null}
          </button>
        );
      })}
    </div>
  );

  const legendItems = () => {
    const base = [
      ["#FF2A2A", "Unexplained anomaly"],
      ["#FF9500", "Explained (known event)"],
    ];
    if (hazard === "seismic") return [...base, ["#B388FF", "Background seismicity"]];
    if (hazard === "fire") return [...base, ["#34D399", "Low fire-weather"]];
    if (hazard === "storm") return [...base, ["#60A5FA", "Storm-index nominal"]];
    if (hazard === "volcano") return [...base, ["#F472B6", "Baseline degassing"]];
    if (hazard === "tsunami") return [...base, ["#2DD4BF", "DART / quake nominal"]];
    if (hazard === "flood") return [...base, ["#0EA5E9", "River discharge nominal"]];
    if (hazard === "landslide") return [...base, ["#C2703D", "Slope / GLOF nominal"]];
    if (hazard === "all")
      return [
        ...base,
        ["#B388FF", "Seismic"],
        ["#34D399", "Fire-weather"],
        ["#60A5FA", "Storm"],
        ["#F472B6", "Volcano SO₂"],
        ["#2DD4BF", "Tsunami"],
        ["#0EA5E9", "Flood"],
        ["#C2703D", "Landslide / GLOF"],
      ];
    return [
      ...base,
      ["#00F0FF", "Nominal baseline"],
    ];
  };

  // Vigilo Earth displays only anomalies (nominal baseline hidden), but the country list must still
  // reflect full global coverage — so it is derived from ALL observations.
  const anomaliesOnly = observations.filter((o) => o.triage_status !== "nominal");
  const byHazard = hazard === "all" ? anomaliesOnly : anomaliesOnly.filter((o) => getHazard(o) === hazard);
  const visibleObs = country === "all" ? byHazard : byHazard.filter((o) => o.country === country);
  const regionDef = REGIONS.find((r) => r.id === region);
  const countries = Array.from(
    new Set([
      ...countryList.filter((c) => !regionDef?.bounds || inRegion(c.lat, c.lon, regionDef.bounds)).map((c) => c.name),
      ...(regionDef?.bounds ? [] : observations.map((o) => o.country).filter(Boolean)),
    ])
  ).sort();
  const allIncidents = buildIncidents(visibleObs);
  const incidents = allIncidents.filter((i) => i.tier === "main");
  const monitoring = allIncidents.filter((i) => i.tier === "monitoring");
  const expiredCount = visibleObs.filter((o) => o.feed?.tier === "expired").length;
  const listed = ledgerTab === "main" ? incidents : monitoring;
  const mainPts = (showAllInc ? listed : listed.slice(0, TOP_N)).flatMap((i) => i.members);
  const monPts = showMon && ledgerTab === "main" ? monitoring.flatMap((i) => i.members) : [];
  const mapObs = [...mainPts, ...monPts];
  const visibleUids = new Set(visibleObs.map((o) => o.uid));
  const visibleHyps =
    hazard === "all" ? hypotheses : hypotheses.filter((h) => visibleUids.has(h.observation_uid));

  // Best (highest-confidence) hypothesis per observation, for the inline "Vigilo Earth read" preview.
  const hypByObs = {};
  for (const h of visibleHyps) {
    const score = h.calibrated_score ?? h.confidence_score ?? 0;
    const cur = hypByObs[h.observation_uid];
    if (!cur || score > (cur.calibrated_score ?? cur.confidence_score ?? 0)) hypByObs[h.observation_uid] = h;
  }

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-atlas-void text-atlas-foreground">
      <LeadTimeBanner alerts={alerts} onSelect={focusAlert} onOpen={() => openPanel(setAlertsOpen)} />
      <TelemetryHeader
        telemetry={telemetry}
        incidentCount={incidents.length}
        alertCount={alerts.filter((a) => !a.acknowledged).length}
        onOpenAlerts={() => openPanel(setAlertsOpen)}
        onOpenCalibration={() => openPanel(setCalibOpen)}
        onOpenBroadcasts={() => openPanel(setBroadcastsOpen)}
        onOpenForesight={() => openPanel(setForesightOpen)}
        foresightCount={foresightCount}
      />


      <main className="flex-1 flex overflow-hidden">
        <section className="flex-1 min-w-0 relative h-full border-r border-white/10 grid-backdrop overflow-hidden" data-testid="map-section">
          {!booting && (
            <TacticalMap
              points={mapObs}
              onPointClick={(d) => selectObs(d)}
              selectedUid={activeUid}
              viewMode={viewMode}
              focus={focus}
              basemap={basemap}
              shakemaps={showShake ? shakemaps : []}
              cyclones={showCyc ? cyclones : []}
              reported={showRep ? reported : []}
              felt={showFelt ? felt : []}
              dams={showDam ? dams : []}
              onCycloneSelect={selectCyclone}
              onShakeSelect={selectShake}
              rain={showRain && rainMeta ? { meta: rainMeta, mode: rainMode, opacity: rainOpacity } : null}
            />
          )}

          <div className="absolute top-3 left-4 z-[1000]">
            <HazardToggle />
          </div>

          <div className="absolute top-3 right-9 z-[1000]">
            <LayerPanel
              basemap={basemap} setBasemap={setBasemap} viewMode={viewMode} setViewMode={setViewMode}
              cyc={showCyc} setCyc={setShowCyc} cycCount={cyclones.length}
              dam={showDam} setDam={setShowDam} damCount={dams.length} damFlagged={dams.filter((d) => d.level).length}
              rep={showRep} setRep={setShowRep} repCount={reported.length}
              felt={showFelt} setFelt={setShowFelt} feltCount={felt.length}
              shake={showShake} setShake={setShowShake} shakeCount={shakemaps.length}
              mon={showMon} setMon={setShowMon} monCount={monitoring.length}
              rain={showRain} setRain={setShowRain} rainMode={rainMode} setRainMode={setRainMode}
              rainOpacity={rainOpacity} setRainOpacity={setRainOpacity} rainMeta={rainMeta}
            />
          </div>

          <div className="absolute top-14 left-4 z-[1000] flex flex-col gap-1.5 bg-atlas-void/80 backdrop-blur border border-white/10 px-3 py-2 w-max max-w-[calc(100vw-2rem)]">
            <div className="text-[10px] font-mono uppercase tracking-wider whitespace-nowrap text-atlas-secondary leading-tight pb-1.5 mb-0.5 border-b border-white/10" data-testid="map-legend-title">
              {HAZARD_TITLE[hazard]}
            </div>
            {legendItems().map(([c, label]) => (
              <div key={label} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c }} />
                <span className="text-[10px] font-mono text-atlas-secondary">{label}</span>
              </div>
            ))}
          </div>

          <div className="absolute bottom-[4.5rem] sm:bottom-10 left-4 z-[1000] max-w-[calc(100%-5rem)]">
            <RegionMenu value={region} onSelect={selectRegion} />
          </div>

          <BrandStrip />

          <PanelEdgeToggle collapsed={fullMap} onToggle={toggleFullMap} count={incidents.length} />

          {booting && (
            <div className="absolute inset-0 z-[1100] flex flex-col items-center justify-center bg-atlas-void/80 scanlines">
              <img
                src="/vigilo-wordmark-dark.png"
                alt="Vigilo Earth"
                className="w-80 h-auto object-contain mb-6 opacity-95"
                data-testid="boot-logo"
              />
              <Loader2 className="w-6 h-6 text-atlas-cyan animate-spin mb-3" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-atlas-cyan/70">
                Autonomous engine calibrating · first planetary scan in progress
              </span>
              {apiError ? (
                <span className="text-[10px] font-mono text-atlas-red mt-1.5 max-w-md text-center" data-testid="boot-api-error">
                  Cannot reach the Vigilo backend: {apiError}
                </span>
              ) : (
                <span className="text-[10px] font-mono text-atlas-secondary/80 mt-1.5">
                  No action needed — data will appear automatically
                </span>
              )}
            </div>
          )}
        </section>

        <div
          className={`relative h-full shrink-0 overflow-hidden transition-[width] duration-500 ease-out ${fullMap ? "w-0" : "w-[40%]"}`}
          data-testid="ledger-container"
          aria-hidden={fullMap}
        >
          <div className="absolute inset-y-0 right-0 w-[40vw] bg-atlas-void" id="side-panel-slot">
            <DiscoveryLedger
              incidents={incidents}
              monitoring={monitoring}
              expiredCount={expiredCount}
              storms={cyclones}
              tab={ledgerTab}
              setTab={setLedgerTab}
              showAll={showAllInc}
              onToggleShowAll={() => setShowAllInc((v) => !v)}
              hypByObs={hypByObs}
              onSelectObservation={selectObs}
              onFocusIncident={(inc) => setFocus({ bounds: inc.bounds, key: Date.now() })}
              activeUid={activeUid}
              country={country}
              countries={countries}
              onCountryChange={setCountry}
            />
          </div>
        </div>
      </main>

      <DetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        detail={detail}
        loading={detailLoading}
        error={detailError}
      />

      <AlertsDrawer
        open={alertsOpen}
        onOpenChange={setAlertsOpen}
        alerts={alerts}
        onSelect={focusAlert}
        onAck={ackAlert}
        onBroadcast={onBroadcast}
      />

      <CalibrationPanel open={calibOpen} onOpenChange={setCalibOpen} />

      <BroadcastsPanel open={broadcastsOpen} onOpenChange={setBroadcastsOpen} />

      <ForesightPanel open={foresightOpen} onOpenChange={setForesightOpen} />
    </div>
  );
}
